import React, { useRef, useState, useEffect } from 'react'
import { LazyLoadConfig } from './types'
import { buildClassName } from 'src/utils'
import { useIntersectionObserver } from 'src/hooks'
import { Placeholder } from './Placeholder'

export interface LazyLoaderProps extends LazyLoadConfig {
    className?: string
    children: React.ReactNode
}

export default function LazyLoader({
    placeholder,
    className = '',
    threshold = 0.25,
    placeholderWidth = 300,
    placeholderHeight = 300,
    children,
}: LazyLoaderProps) {
    // tracks when the element comes into the viewport
    const [inView, setInView] = useState(false)

    function handlePlaceholderInView(entries: IntersectionObserverEntry[]) {
        // we're creating exactly one IntersectionObserver per element, so just get the first element
        const entry = entries[0]
        if (entry.isIntersecting) {
            setInView(true)
        }
    }

    const placeholderRef = useRef<HTMLElement>(null)
    const observerRef = useIntersectionObserver(handlePlaceholderInView, {
        threshold,
    })

    useEffect(() => {
        const observer = observerRef.current
        const placeholderEl = placeholderRef.current!
        if (observer) {
            observer.observe(placeholderEl)
            return () => {
                observer.unobserve(placeholderEl)
            }
        }
    }, [observerRef])

    if (threshold < 0 || threshold > 1) {
        if (process.env.NODE_ENV === 'development') {
            throw Error(
                'threshold prop must be a number between zero and 1 (a percentage) - see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API'
            )
        }
        return null
    }

    // @NB: if observerRef.current is null, then it's an old browser that doesn't support the IntersectionObserver API
    return inView || !observerRef.current ? (
        <>{children}</>
    ) : (
        <Placeholder
            ref={placeholderRef}
            className={`${buildClassName('Placeholder')} ${className}`}
            placeholder={placeholder}
            placeholderHeight={placeholderHeight}
            placeholderWidth={placeholderWidth}
            threshold={threshold}
        />
    )
}
